<script setup lang="ts">
/**
 * Подзадачи — макет 13:460: заголовок со счётчиком «2 / 3», полоса прогресса
 * и строки с ключом, названием и точкой статуса.
 *
 * Прогресс считаем по категории колонки, а не по названию: «Готово» в одном
 * проекте зовётся «Выкачено», и сравнивать строки значило бы считать
 * неправильно у всех, кто переименовал колонку.
 *
 * Новая подзадача заводится строкой прямо здесь: уходить ради неё в модалку
 * создания и заново выбирать проект, тип и родителя — три лишних шага на
 * действие, которое делают по ходу разбора задачи.
 */
import { AppButton } from '@vinprop/design-system'
import { Plus } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import { isClosedCategory } from '@/api/statusCategory'

import { childTypeOf, createIssue } from '@/features/issues/api'
import { notifyIssueCreated } from '@/features/issues/created'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'

import { listChildren } from '../links'
import { useIssueStore } from '../store'

import type { IssueRow } from '@/features/issues/api'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const children = ref<IssueRow[]>([])
const isAdding = ref(false)
const title = ref('')
const isSaving = ref(false)
const failure = ref('')

const issueId = computed(() => store.issue?.id ?? '')

/** Тип ребёнка: у эпика — задача, у задачи и бага — подзадача, у подзадачи никакой */
const childType = computed(() => childTypeOf(store.issue?.type ?? ''))

/**
 * Иерархия в трекере ровно на три уровня, и ниже подзадачи детей не бывает:
 * сервер такое создание отклоняет. Раньше кнопка стояла и там — набранное
 * название просто исчезало, и это читалось как «подзадачи не сохраняются»
 * (DEV-1593).
 */
const canHaveChildren = computed(() => Boolean(childType.value))

/** Заголовок с полосой готовности показываем, только когда есть что мерить */
const hasList = computed(() => children.value.length > 0 || isAdding.value)

// Закрытые, а не только сделанные: прогресс подзадач отвечает «сколько ещё
// осталось», и отклонённая подзадача работы не оставляет.
const done = computed(
  () =>
    children.value.filter((child) =>
      isClosedCategory(store.columns.find((column) => column.id === child.columnId)?.statusCategory),
    ).length,
)

const progress = computed(() =>
  children.value.length ? Math.round((done.value / children.value.length) * 100) : 0,
)

const load = async () => {
  if (!issueId.value || !store.issue) return

  try {
    children.value = await listChildren(store.issue.projectId, issueId.value)
  } catch (failure) {
    emit('failed', failure)
  }
}

const add = async () => {
  const name = title.value.trim()
  const type = childType.value

  if (!name || !store.issue || !type) return

  isSaving.value = true
  failure.value = ''

  try {
    const child = await createIssue({
      projectId: store.issue.projectId,
      title: name,
      descriptionMd: '',
      type,
      priority: 'PRIORITY_MEDIUM',
      parentId: issueId.value,
    })

    // Ключ подзадачи виден только в списке ниже — плашка называет его сразу и
    // даёт открыть, не выискивая новую строку глазами (DEV-2074).
    notifyIssueCreated(child.key)

    title.value = ''
    isAdding.value = false
    await load()
  } catch (error) {
    // Отказ показываем в самом блоке и строку не закрываем: тост над экраном
    // мимо набранного названия читается как «ничего не произошло».
    failure.value = errorMessage(error, t('issue.subtasks.failed'))
    emit('failed', error)
  } finally {
    isSaving.value = false
  }
}

/** Точка красится цветом колонки — тем же, что в пилюле статуса и на доске */
const dotColor = (child: IssueRow) =>
  store.columns.find((column) => column.id === child.columnId)?.color || 'var(--color-text-muted)'

onMounted(load)

watch(issueId, load)
</script>

<template>
  <!-- Блока нет вовсе, когда детей нет и завести их некому: ниже подзадачи
       уровня не существует. -->
  <section v-if="hasList || canHaveChildren" class="isub">
    <template v-if="hasList">
      <header class="isub__head">
        <h2 class="isub__title">{{ t('issue.subtasks.title') }}</h2>
        <span class="isub__count">{{ done }} / {{ children.length }}</span>
      </header>

      <div class="isub__bar">
        <span class="isub__fill" :style="{ width: `${progress}%` }" />
      </div>

      <RouterLink
        v-for="child in children"
        :key="child.id"
        class="isub__row"
        :to="`/issue/${child.key}`"
      >
        <IssueTypeIcon :type="child.type" />
        <span class="isub__key">{{ child.key }}</span>
        <span class="isub__name">{{ child.title }}</span>
        <span class="isub__dot" :style="{ background: dotColor(child) }" />
      </RouterLink>
    </template>

    <template v-if="canHaveChildren">
      <div v-if="isAdding" class="isub__add">
        <input
          v-model="title"
          class="isub__field"
          type="text"
          :placeholder="t('issue.subtasks.placeholder')"
          :aria-label="t('issue.subtasks.placeholder')"
          @keydown.enter="add"
          @keydown.esc="isAdding = false"
        />
        <AppButton variant="primary" size="small" :loading="isSaving" @click="add">
          {{ t('issues.create.submit') }}
        </AppButton>
      </div>

      <button
        v-else
        type="button"
        class="isub__new"
        :class="{ 'isub__new--alone': !hasList }"
        @click="isAdding = true"
      >
        <Plus :size="14" />
        {{ t('issue.subtasks.add') }}
      </button>

      <p v-if="failure" class="isub__error">{{ failure }}</p>
    </template>
  </section>
</template>

<style scoped>
.isub {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.isub__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.isub__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-semibold);
  line-height: 17px;
}

.isub__count {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}

/* Полоса 4px во всю ширину — как в макете: доля сделанного видна раньше, чем
   прочитан счётчик. */
.isub__bar {
  height: 4px;
  border-radius: var(--radius-full);
  background: var(--color-bg-muted);
  overflow: hidden;
}

.isub__fill {
  display: block;
  height: 100%;
  background: var(--color-success);
  transition: width 0.2s ease;
}

.isub__row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 29px;
  color: inherit;
  text-decoration: none;
}

.isub__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  line-height: 16px;
}

.isub__name {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.isub__row:hover .isub__name {
  text-decoration: underline;
}

.isub__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.isub__add {
  display: flex;
  gap: 8px;
}

.isub__field {
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.isub__new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.isub__new:hover {
  color: var(--color-primary);
}

/* Когда подзадач нет, кнопка стоит одна — без заголовка и пустой полосы */
.isub__new--alone {
  margin-top: -4px;
}

.isub__error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-body-sm);
}
</style>
